import {
    useFetchAnswerCategoryMismatchPredictionsQuery,
    useFetchQuestionAnswersQuery,
    useFetchTextAssignmentSubmissionsQuery,
} from '../../../../../store/apis/submission.api'
import { useParams } from 'react-router'
import { getReviewStatusName, SubmissionReviewStatuses } from '../../../../../constants/submission-review-statuses'
import { useVersionId } from '../../../../../utils/parseURL.helper'
import { useSelector } from 'react-redux'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import { isMba } from '../../../../../utils/assignment.helper'
import { isNumber } from 'lodash'
import userSelectors from '../../../../../store/selectors/user.selectors'
import useQueryParam from '../../../../../hooks/useQueryParam'
import { TEST_SET_ONLY_PARAM } from '../../../../../constants/query-params'

const calculateReviewedSubmissionsStatsForQuestion = (submissions, answerCategoryMismatchPredictions) => {
    const filteredSubmissions = !answerCategoryMismatchPredictions
        ? submissions
        : submissions?.filter(submission =>
              answerCategoryMismatchPredictions.some(
                  mismatchPrediction => mismatchPrediction.answerId === submission.answerId,
              ),
          )

    const reviewed = filteredSubmissions?.filter(submission => submission.approveStatus).length
    const reviewedPercentage = filteredSubmissions?.length
        ? Math.round((reviewed / filteredSubmissions?.length) * 100)
        : 100

    const unreviewed = filteredSubmissions?.filter(submission => !submission.approveStatus).length
    const unreviewedPercentage = filteredSubmissions?.length
        ? Math.round((unreviewed / filteredSubmissions?.length) * 100)
        : 100
    const total = filteredSubmissions?.length

    return {
        total,
        reviewed,
        reviewedPercentage,
        unreviewed,
        unreviewedPercentage,
    }
}

const calculateReviewedSubmissionsStatsForAllQuestions = submissions => {
    const totalSubmissions = submissions?.length || 0
    const groupsDistribution = Object.values(SubmissionReviewStatuses)?.map(reviewStatus => {
        const groupSubmissions = submissions?.filter(submission => submission.reviewStatus === reviewStatus) || []
        const percentage = totalSubmissions > 0 ? (groupSubmissions.length / totalSubmissions) * 100 : 0

        return {
            title: getReviewStatusName(reviewStatus),
            reviewStatus,
            percentage: parseFloat(percentage.toFixed(2)),
            total: groupSubmissions.length,
        }
    })

    const reviewed = groupsDistribution.find(group => group.reviewStatus === SubmissionReviewStatuses.REVIEWED).total
    const reviewedPercentage = Math.round(
        groupsDistribution.find(group => group.reviewStatus === SubmissionReviewStatuses.REVIEWED)?.percentage || 0,
    )

    const partiallyReviewed = groupsDistribution.find(
        group => group.reviewStatus === SubmissionReviewStatuses.PARTIALLY_REVIEWED,
    ).total
    const partiallyReviewedPercentage = Math.round(
        groupsDistribution.find(group => group.reviewStatus === SubmissionReviewStatuses.PARTIALLY_REVIEWED)
            ?.percentage || 0,
    )

    const unreviewed = groupsDistribution.find(
        group => group.reviewStatus === SubmissionReviewStatuses.UNREVIEWED,
    ).total
    const unreviewedPercentage = Math.round(
        groupsDistribution.find(group => group.reviewStatus === SubmissionReviewStatuses.UNREVIEWED)?.percentage || 0,
    )

    return {
        total: totalSubmissions,
        reviewed,
        reviewedPercentage,
        partiallyReviewed,
        partiallyReviewedPercentage,
        unreviewed,
        unreviewedPercentage,
    }
}

const useReviewedSubmissionsStats = () => {
    const { layerId: questionRubricIdParam, token } = useParams()
    const versionId = useVersionId()
    const questionRubricId = ['all', 'dashboard'].includes(questionRubricIdParam) ? null : parseInt(questionRubricIdParam)
    const assignmentType = useSelector(assignmentSelectors.type)
    const userId = useSelector(userSelectors.id)
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const testSetOnlyParam = useQueryParam(TEST_SET_ONLY_PARAM) === 'true'

    const { data: submissions, isLoading: isSubmissionsLoading, error: submissionsError } = useFetchTextAssignmentSubmissionsQuery(
        { token, versionId, testSetOnly: testSetOnlyParam },
        { skip: questionRubricId !== null },
    )
    const { data: questionSubmissions, isLoading: isQuestionSubmissionsLoading, error: questionSubmissionsError } = useFetchQuestionAnswersQuery(
        { id: questionRubricId, versionId, assigneeIds: isOwner ? undefined : [userId], testSetOnly: testSetOnlyParam },
        { skip: questionRubricId === null },
    )

    const { data: answerCategoryMismatchPredictions, isLoading: isAnswerCategoryMismatchPredictionsLoading, error: answerCategoryMismatchPredictionsError } = useFetchAnswerCategoryMismatchPredictionsQuery(
        { questionRubricId },
        {
            skip: !isNumber(questionRubricId) || isMba(assignmentType),
        },
    )

    const isLoading = isSubmissionsLoading || isQuestionSubmissionsLoading || isAnswerCategoryMismatchPredictionsLoading
    const error = submissionsError || questionSubmissionsError || answerCategoryMismatchPredictionsError

    const stats = questionRubricId === null
        ? calculateReviewedSubmissionsStatsForAllQuestions(submissions)
        : calculateReviewedSubmissionsStatsForQuestion(questionSubmissions, answerCategoryMismatchPredictions)

    return {
        ...stats,
        isLoading,
        error,
    }
}

export default useReviewedSubmissionsStats
